import React, { useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';

import {
  Alert,
  Box,
  CircularProgress,
  LinearProgress,
  MenuItem,
  Stack,
  Tab,
  TablePagination,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { useGetTeamQueueCases } from '~/hooks/apis/contracting/useGetTeamQueueCases';

import type { ContractingCaseStatusFilter } from '~/types/services/contracting';

import CasesAllPage from './CasesAllPage';
import CasesByOwnerPage from './CasesByOwnerPage';
import { casesErrorMessage } from './casesErrorMessage';
import {
  CAMPAIGN_OPS_TEAM_KEY,
  CASES_LABEL,
  CASE_STATUS_FILTERS,
  DEFAULT_CASE_STATUS_FILTER,
  casePath,
} from './constants';

const LIST_TABS = [
  { value: 'all', label: 'All Cases' },
  { value: 'owner', label: 'Per Owner' },
] as const;

type ListTab = (typeof LIST_TABS)[number]['value'];

// svc-contracting's largest page; the open queue fits in one.
const PAGE_SIZE = 100;
// svc-contracting can't page past 2,000 cases (Salesforce's SOQL OFFSET limit).
const MAX_PAGEABLE_CASES = 2000;

export default function CasesListPage() {
  const [tab, setTab] = useState<ListTab>('all');
  const [status, setStatus] = useState<ContractingCaseStatusFilter>(DEFAULT_CASE_STATUS_FILTER);
  const [page, setPage] = useState(0);
  const { data, isLoading, isFetching, isPlaceholderData, error } = useGetTeamQueueCases(CAMPAIGN_OPS_TEAM_KEY, {
    status,
    offset: page * PAGE_SIZE,
    limit: PAGE_SIZE,
  });
  const cases = data?.result ?? [];
  const pageableCount = Math.min(data?.pagination.total_items ?? 0, MAX_PAGEABLE_CASES);

  // Cases closing while someone is on the last page can leave it empty, so step back to the new last page.
  useEffect(() => {
    const lastPage = Math.max(0, Math.ceil(pageableCount / PAGE_SIZE) - 1);
    if (data && page > lastPage) {
      setPage(lastPage);
    }
  }, [data, page, pageableCount]);

  const handleStatusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const option = CASE_STATUS_FILTERS.find(({ value }) => value === event.target.value);
    if (option) {
      setStatus(option.value);
      setPage(0);
    }
  };

  return (
    <Box>
      <Helmet>
        <title>{CASES_LABEL}</title>
      </Helmet>
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Tabs value={tab} onChange={(_, value) => setTab(value)} aria-label="Campaign Operation Cases views">
          {LIST_TABS.map((entry) => (
            <Tab key={entry.value} value={entry.value} label={entry.label} />
          ))}
        </Tabs>
        <TextField select size="small" label="Status" value={status} onChange={handleStatusChange} sx={{ width: 160 }}>
          {CASE_STATUS_FILTERS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box mt={2}>
        {isLoading && <CircularProgress />}
        {isFetching && !isLoading && <LinearProgress aria-label="Loading cases" />}
        {error && <Alert severity="error">{casesErrorMessage(error, 'cases')}</Alert>}
        {data && (
          <Box sx={{ opacity: isPlaceholderData ? 0.5 : 1 }}>
            {tab === 'owner' && data.pagination.total_items > cases.length && (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, pt: 1 }}>
                Grouping and searching the {cases.length} cases on this page of {data.pagination.total_items}.
              </Typography>
            )}
            {tab === 'all' ? (
              <CasesAllPage cases={cases} buildCaseHref={casePath} />
            ) : (
              <CasesByOwnerPage cases={cases} buildCaseHref={casePath} />
            )}
            <TablePagination
              component="div"
              count={pageableCount}
              page={page}
              rowsPerPage={PAGE_SIZE}
              rowsPerPageOptions={[PAGE_SIZE]}
              onPageChange={(_, nextPage) => setPage(nextPage)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
